"use client";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

// Define the schema with zod for validation
const schema = z.object({
  firstName: z.string().min(1, { message: "First name is required" }),
  lastName: z.string().min(1, { message: "Last name is required" }),
  email: z
    .string()
    .email({ message: "Invalid email address" })
    .min(1, { message: "Email is required" }),
  subject: z.string().min(1, { message: "Subject is required" }),
  message: z.string().min(1, { message: "Message is required" }),
});

const ContactForm = () => {
  const [isSending, setIsSending] = useState(false); // State for form submission
  const [responseMessage, setResponseMessage] = useState(""); // State for success/error messages

  // Set up react-hook-form with zod validation
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
  });

  // Form submission handler
  const onSubmit = async (data) => {
    setIsSending(true); // Start sending
    setResponseMessage(""); // Reset message state

    try {
      const response = await fetch("/api/sendEmail", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setResponseMessage("Message sent successfully.");
        reset(); // Reset form fields
      } else {
        throw new Error("Failed to send message");
      }
    } catch (error) {
      setResponseMessage(error.message);
    } finally {
      setIsSending(false); // Done sending
    }
  };

  return (
    <div>
      {responseMessage && (
        <p className="mt-4 text-center p-2 m-2 rounded-lg bg-green-300">
          {responseMessage}
        </p>
      )}

      <h2 className="text-3xl text-gray-900 mb-8">Request Our Services</h2>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <input
              type="text"
              placeholder="First Name"
              className="border border-gray-300 p-3 w-full rounded-md"
              {...register("firstName")}
            />
            {errors.firstName && (
              <p className="text-red-500">{errors.firstName.message}</p>
            )}
          </div>
          <div>
            <input
              type="text"
              placeholder="Last Name"
              className="border border-gray-300 p-3 w-full rounded-md"
              {...register("lastName")}
            />
            {errors.lastName && (
              <p className="text-red-500">{errors.lastName.message}</p>
            )}
          </div>
        </div>
        <div className="mt-4">
          <input
            type="email"
            placeholder="Your Email"
            className="border border-gray-300 p-3 w-full rounded-md"
            {...register("email")}
          />
          {errors.email && (
            <p className="text-red-500">{errors.email.message}</p>
          )}
        </div>
        <div className="mt-4">
          <input
            type="text"
            placeholder="How can we help you?"
            className="border border-gray-300 p-3 w-full rounded-md"
            {...register("subject")}
          />
          {errors.subject && (
            <p className="text-red-500">{errors.subject.message}</p>
          )}
        </div>
        <div className="mt-4">
          <textarea
            placeholder="Comments / Questions"
            className="border border-gray-300 p-3 w-full h-32 rounded-md"
            {...register("message")}
          ></textarea>
          {errors.message && (
            <p className="text-red-500">{errors.message.message}</p>
          )}
        </div>
        <div className="mt-6">
          <button
            type="submit"
            className="bg-black text-white px-6 py-2 rounded-md hover:bg-gray-800"
            disabled={isSending}
          >
            {isSending ? "Sending..." : "Send Message"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
